import { Datepicker, locale } from "@mobiscroll/react";
import { DateTime } from "luxon";
import React from "react";
import { useTranslation } from "react-i18next";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { calMinuteInterval } from "../BookingForm/functions/date-time.functions";

type P = {
  chosenTimes: {
    startTime: string;
    endTime: string;
  };
  setChosenTimes: React.Dispatch<
    React.SetStateAction<{
      startTime: string;
      endTime: string;
    }>
  >;
  editable: {
    start: boolean;
    end: boolean;
  };
  /** yyyy-MM-dd used to build the start time value (defaults to today). */
  startAnchorDateIso?: string;
  /** yyyy-MM-dd used to build the end time value (defaults to today). */
  endAnchorDateIso?: string;
};

export const TimePickers: React.FC<P> = ({
  chosenTimes,
  setChosenTimes,
  editable,
  endAnchorDateIso,
  startAnchorDateIso
}) => {
  const { t, i18n } = useTranslation();
  const { timeFormat } = useTimeFormat();

  const startDayIso = startAnchorDateIso ?? DateTime.now().toFormat("yyyy-MM-dd");
  const endDayIso = endAnchorDateIso ?? DateTime.now().toFormat("yyyy-MM-dd");
  const todayIso = DateTime.now().toFormat("yyyy-MM-dd");
  const startTimeValidRange =
    startDayIso === todayIso
      ? [
          {
            start: DateTime.local()
              .set({ minute: calMinuteInterval(DateTime.local()) })
              .toFormat("HH:mm"),
            end: "23:59"
          }
        ]
      : [{ start: "00:00", end: "23:59" }];

  return (
    <>
      <div data-testid="time-picker-start-container">
        <Datepicker
          data-testid={"time-picker-start"}
          locale={locale[i18n.language]}
          controls={["time"]}
          timeFormat={timeFormat}
          onChange={ev => {
            const startDate = DateTime.fromJSDate(ev.value as Date).toFormat("HH:mm");
            setChosenTimes(prevState => ({ ...prevState, startTime: startDate }));

            const resStart = DateTime.fromJSDate(ev.value as Date);
            const initialEnd = DateTime.fromISO(`${endDayIso}T${chosenTimes.endTime}`);

            if (resStart >= initialEnd) {
              setChosenTimes(prevState => ({
                ...prevState,
                startTime: startDate,
                endTime: startDate
              }));
            }
          }}
          value={DateTime.fromISO(`${startDayIso}T${chosenTimes.startTime}`).toFormat("HH:mm")}
          stepMinute={15}
          label={t("Starting From")}
          disabled={!editable.start}
          valid={startTimeValidRange}
        />
      </div>
      <div data-testid="time-picker-end-container">
        <Datepicker
          data-testid={"time-picker-end"}
          locale={locale[i18n.language]}
          controls={["time"]}
          timeFormat={timeFormat}
          onChange={ev => {
            const date = DateTime.fromJSDate(ev.value as Date).toFormat("HH:mm");
            setChosenTimes(prevState => ({ ...prevState, endTime: date }));
          }}
          value={DateTime.fromISO(`${endDayIso}T${chosenTimes.endTime}`).toFormat("HH:mm")}
          stepMinute={15}
          label={t("Ending At")}
          disabled={!editable.end}
          valid={[
            {
              start: chosenTimes.startTime,
              end: "23:59"
            }
          ]}
        />
      </div>
    </>
  );
};
